import React, { useState } from "react";
import {
    Award, Truck, BadgeCheck, ArrowRight,
    Snowflake, CloudSun, Disc3, Droplets, Settings2, CircleDot, Car, Wrench,
    Phone, Send, Building2, MapPin, Navigation,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { submitServiceRequest, submitWholesaleQuote } from "../lib/api";

const SERVICES = [
    { name: "Tires", icon: CircleDot, body: "All tire sizes available — all-weather and winter. Sourced fast, fitted right.", num: "01" },
    { name: "Installations", icon: Wrench, body: "Mounting, balancing and seasonal swaps done while you wait.", num: "02" },
    { name: "Brakes", icon: Disc3, body: "Pads, rotors and calipers — inspected, replaced and road-tested.", num: "03" },
    { name: "Suspension", icon: Car, body: "Shocks, struts and alignment for a planted, rattle-free ride.", num: "04" },
    { name: "Oil Changes", icon: Droplets, body: "Quick full-synthetic and conventional oil changes with multi-point check.", num: "05" },
    { name: "Repairs", icon: Settings2, body: "General mechanical repairs and diagnostics for all makes and models.", num: "06" },
];

export default function Landing() {
    return (
        <div className="bg-background min-h-screen">
            <Navbar />
            <HeroSection />
            <TiresStrip />
            <ServicesSection />
            <ContactSection />
            <WholesaleSection />
            <LocationSection />
            <TrustSection />
            <Footer />
            <FloatingCallButton />
        </div>
    );
}

const SHOP_ADDRESS = "130 Westmore Dr, Unit 6, Etobicoke, Toronto, ON, Canada";
const MAPS_QUERY = encodeURIComponent(SHOP_ADDRESS);

function FloatingCallButton() {
    return (
        <a
            href="tel:+16476280646"
            data-testid="floating-call-button"
            aria-label="Call TreadMasters"
            className="md:hidden fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-orange-600 hover:bg-orange-500 text-white flex items-center justify-center shadow-lg shadow-black/50 glow-orange animate-fade-up"
        >
            <Phone size={22} strokeWidth={2} />
        </a>
    );
}

function LocationSection() {
    return (
        <section id="location" className="py-28 bg-zinc-950 border-y border-zinc-900 grain relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <span className="font-mono uppercase text-[11px] tracking-[0.4em] text-orange-500">
                        Visit the Shop
                    </span>
                    <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase text-white mt-4 leading-tight">
                        Drive in. Roll out.
                    </h2>
                    <div className="mt-8 flex items-start gap-4">
                        <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0">
                            <MapPin size={20} className="text-orange-500" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="font-display uppercase tracking-[0.18em] text-white">Address</div>
                            <p className="text-zinc-400 mt-2 leading-relaxed" data-testid="shop-address">
                                130 Westmore Dr, Unit 6<br />Etobicoke, Toronto, ON, Canada
                            </p>
                        </div>
                    </div>
                    <div className="mt-6 flex items-start gap-4">
                        <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0">
                            <Phone size={20} className="text-orange-500" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="font-display uppercase tracking-[0.18em] text-white">Phone</div>
                            <a href="tel:+16476280646" className="text-zinc-400 hover:text-orange-500 mt-2 inline-block">647-628-0646</a>
                        </div>
                    </div>
                    <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="get-directions-btn"
                        className="mt-10 inline-flex bg-orange-600 hover:bg-orange-500 text-white font-display uppercase tracking-[0.18em] text-sm px-8 py-4 rounded-sm transition-all glow-orange items-center gap-3"
                    >
                        Get Directions <Navigation size={15} />
                    </a>
                </div>
                <div className="lg:col-span-7">
                    <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-900" data-testid="shop-map">
                        <iframe
                            title="TreadMasters location map"
                            src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`}
                            className="w-full h-[380px] sm:h-[440px]"
                            style={{ border: 0, filter: "grayscale(30%) contrast(1.05)" }}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroSection() {
    return (
        <section className="relative min-h-[100vh] flex items-end pb-32 pt-44 overflow-hidden grain">
            <div className="absolute inset-0">
                <img
                    src="https://images.pexels.com/photos/116676/pexels-photo-116676.jpeg"
                    alt="Tire tread close-up"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                    <div className="flex items-center gap-3 mb-6 animate-fade-up">
                        <span className="h-px w-10 bg-orange-600" />
                        <span className="font-mono uppercase text-[11px] tracking-[0.4em] text-orange-500">
                            Tires · Auto Services · Wholesale
                        </span>
                    </div>
                    <h1 className="font-industrial text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-[-0.03em] animate-fade-up">
                        Master <br />
                        <span className="text-stroke">Every</span> <span className="text-orange-500">Road.</span>
                    </h1>
                    <p className="mt-8 text-zinc-300 text-lg max-w-xl leading-relaxed animate-fade-up">
                        All tire sizes available — all-weather and winter. Installations, brakes,
                        suspension, oil changes and repairs under one roof. Contact us for
                        installations and seasonal swaps.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 animate-fade-up">
                        <a
                            href="#contact"
                            data-testid="hero-book-service"
                            className="bg-orange-600 hover:bg-orange-500 text-white font-display uppercase tracking-[0.18em] text-sm px-8 py-4 rounded-sm transition-all glow-orange flex items-center gap-3 group"
                        >
                            Book a Service
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </a>
                        <a
                            href="#wholesale"
                            data-testid="hero-wholesale"
                            className="bg-transparent border border-zinc-600 hover:border-white text-white font-display uppercase tracking-[0.18em] text-sm px-8 py-4 rounded-sm transition-colors flex items-center gap-3"
                        >
                            Wholesale Inquiry
                        </a>
                    </div>

                    <div className="mt-14 grid grid-cols-3 gap-8 max-w-lg">
                        <Stat value="All" label="Tire Sizes" />
                        <Stat value="6" label="Services" />
                        <Stat value="GTA" label="Coverage" />
                    </div>
                </div>
            </div>

            <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-3 items-center font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                <span className="rotate-90 origin-center mt-12">Scroll</span>
                <div className="h-16 w-px bg-zinc-700 mt-12" />
            </div>
        </section>
    );
}

function TiresStrip() {
    return (
        <section id="tires" className="relative -mt-20 z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-zinc-900/95 border border-zinc-700/70 backdrop-blur-xl p-8 sm:p-10 rounded-sm grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="tires-strip">
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0">
                        <CircleDot size={20} className="text-orange-500" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-display uppercase tracking-[0.18em] text-white">All Sizes Available</div>
                        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">Every width, aspect ratio and rim diameter — sourced on demand.</p>
                    </div>
                </div>
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0 relative">
                        <CloudSun size={20} className="text-orange-500" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-display uppercase tracking-[0.18em] text-white">All-Weather & Winter</div>
                        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">All-weather and winter sizes in stock — all 3PMSF (3 Peak Mountain Snowflake) certified.</p>
                    </div>
                </div>
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0">
                        <Snowflake size={20} className="text-orange-500" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-display uppercase tracking-[0.18em] text-white">Install & Swap</div>
                        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                            Please <a href="#contact" className="text-orange-500 hover:text-orange-400 underline underline-offset-4" data-testid="strip-contact-link">contact us</a> for installations and seasonal swapping.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ServicesSection() {
    return (
        <section id="services" className="py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                <div>
                    <span className="font-mono uppercase text-[11px] tracking-[0.4em] text-orange-500">
                        What We Do / 06 Services
                    </span>
                    <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase text-white mt-4 max-w-xl leading-tight">
                        Full service. One shop.
                    </h2>
                </div>
                <a
                    href="#contact"
                    className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-orange-500 flex items-center gap-2"
                    data-testid="services-book-link"
                >
                    Book a service <ArrowRight size={14} />
                </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800">
                {SERVICES.map((s) => (
                    <ServiceCard key={s.name} service={s} />
                ))}
            </div>
        </section>
    );
}

function ServiceCard({ service: s }) {
    const Icon = s.icon;
    return (
        <div className="bg-zinc-900 p-10 group hover:bg-zinc-800/60 transition-colors" data-testid={`service-card-${s.name.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center justify-between mb-8">
                <div className="h-14 w-14 border border-zinc-700 flex items-center justify-center group-hover:border-orange-600 transition-colors">
                    <Icon size={24} className="text-orange-500" strokeWidth={1.5} />
                </div>
                <span className="font-mono text-zinc-700 text-sm tracking-widest">/ {s.num}</span>
            </div>
            <h3 className="font-display uppercase text-2xl tracking-wide text-white">{s.name}</h3>
            <p className="text-zinc-400 mt-4 leading-relaxed">{s.body}</p>
        </div>
    );
}

function ContactSection() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", tire_size: "", service: "Tires", message: "" });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitServiceRequest({
                ...form,
                email: form.email || null,
                tire_size: form.tire_size || null,
                message: form.message || null,
            });
            toast.success("Request received", { description: "We'll call you back shortly to confirm your booking." });
            setForm({ name: "", phone: "", email: "", tire_size: "", service: "Tires", message: "" });
        } catch {
            toast.error("Something went wrong", { description: "Please try again or call us at 647-628-0646." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-28 bg-zinc-950 border-y border-zinc-900 grain relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="font-mono uppercase text-[11px] tracking-[0.4em] text-orange-500">
                        Book a Service
                    </span>
                    <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase text-white mt-4 leading-tight">
                        Tires, installs & swaps — let's get you booked.
                    </h2>
                    <p className="text-zinc-400 mt-6 leading-relaxed max-w-md">
                        Tell us your vehicle's tire size and what you need. We'll confirm
                        availability and call you back with a time.
                    </p>
                    <div className="mt-8 flex items-center gap-3 text-zinc-300">
                        <div className="h-11 w-11 border border-zinc-700 flex items-center justify-center">
                            <Phone size={16} className="text-orange-500" />
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Prefer to call?</div>
                            <div className="font-display tracking-wider text-lg">647-628-0646</div>
                        </div>
                    </div>
                </div>
                <form onSubmit={onSubmit} className="lg:col-span-7 bg-zinc-900 border border-zinc-800 p-8 sm:p-10 rounded-sm" data-testid="service-request-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Name *">
                            <input required value={form.name} onChange={set("name")} data-testid="service-name-input" placeholder="Your name" className={inputCls} />
                        </Field>
                        <Field label="Phone *">
                            <input required value={form.phone} onChange={set("phone")} data-testid="service-phone-input" placeholder="647-000-0000" className={inputCls} />
                        </Field>
                        <Field label="Email">
                            <input type="email" value={form.email} onChange={set("email")} data-testid="service-email-input" placeholder="you@road.com" className={inputCls} />
                        </Field>
                        <Field label="Tire Size (if known)">
                            <input value={form.tire_size} onChange={set("tire_size")} data-testid="service-tiresize-input" placeholder="e.g. 225/45R18" className={inputCls} />
                        </Field>
                        <Field label="Service *" full>
                            <select value={form.service} onChange={set("service")} data-testid="service-select" className={inputCls}>
                                {SERVICES.map((s) => (
                                    <option key={s.name} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Message" full>
                            <textarea rows={3} value={form.message} onChange={set("message")} data-testid="service-message-input" placeholder="Tell us about your vehicle or what you need…" className={inputCls} />
                        </Field>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="service-submit"
                        className="mt-8 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-display uppercase tracking-[0.18em] text-sm px-8 py-4 rounded-sm glow-orange flex items-center gap-3"
                    >
                        {loading ? "Sending…" : <>Request Booking <Send size={15} /></>}
                    </button>
                </form>
            </div>
        </section>
    );
}

const inputCls = "w-full bg-black border border-zinc-700 focus:border-orange-600 focus:outline-none text-white font-mono text-sm px-4 py-3 rounded-sm transition-colors appearance-none";

function Field({ label, children, full = false }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">{label}</div>
            {children}
        </div>
    );
}

function WholesaleSection() {
    const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", sizes_needed: "", message: "" });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitWholesaleQuote({
                ...form,
                phone: form.phone || null,
                message: form.message || null,
            });
            toast.success("Quote request received", { description: "Our wholesale team will reach out within one business day." });
            setForm({ business_name: "", contact_name: "", email: "", phone: "", sizes_needed: "", message: "" });
        } catch {
            toast.error("Something went wrong", { description: "Please check your details and try again." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="wholesale" className="py-28 bg-zinc-950 border-y border-zinc-900 grain relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="font-mono uppercase text-[11px] tracking-[0.4em] text-orange-500">
                        Wholesale Program
                    </span>
                    <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase text-white mt-4 leading-tight">
                        Bulk tires for shops & fleets.
                    </h2>
                    <p className="text-zinc-400 mt-6 leading-relaxed max-w-md">
                        Garages, dealerships and fleet operators — get wholesale pricing direct
                        from the warehouse. Tell us the sizes and quantities you move, and we'll
                        quote you fast.
                    </p>
                    <ul className="mt-8 space-y-4">
                        {["All sizes, all-weather & winter", "3PMSF certified snow-rated tires", "Volume pricing tiers", "Fast GTA delivery"].map((b) => (
                            <li key={b} className="flex items-center gap-3 text-zinc-300">
                                <Building2 size={16} className="text-orange-500 shrink-0" strokeWidth={1.5} />
                                <span className="font-display uppercase tracking-[0.12em] text-sm">{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <form onSubmit={onSubmit} className="lg:col-span-7 bg-zinc-900 border border-zinc-800 p-8 sm:p-10 rounded-sm" data-testid="wholesale-quote-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Business Name *">
                            <input required value={form.business_name} onChange={set("business_name")} data-testid="wholesale-business-input" placeholder="Your shop or fleet" className={inputCls} />
                        </Field>
                        <Field label="Contact Name *">
                            <input required value={form.contact_name} onChange={set("contact_name")} data-testid="wholesale-contact-input" placeholder="Full name" className={inputCls} />
                        </Field>
                        <Field label="Email *">
                            <input required type="email" value={form.email} onChange={set("email")} data-testid="wholesale-email-input" placeholder="purchasing@yourshop.com" className={inputCls} />
                        </Field>
                        <Field label="Phone">
                            <input value={form.phone} onChange={set("phone")} data-testid="wholesale-phone-input" placeholder="647-000-0000" className={inputCls} />
                        </Field>
                        <Field label="Sizes & Quantities *" full>
                            <textarea required rows={3} value={form.sizes_needed} onChange={set("sizes_needed")} data-testid="wholesale-sizes-input" placeholder="e.g. 225/45R18 × 40, 205/55R16 × 100 (winter)…" className={inputCls} />
                        </Field>
                        <Field label="Message" full>
                            <textarea rows={2} value={form.message} onChange={set("message")} data-testid="wholesale-message-input" placeholder="Timelines, delivery needs, anything else…" className={inputCls} />
                        </Field>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="wholesale-submit"
                        className="mt-8 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-display uppercase tracking-[0.18em] text-sm px-8 py-4 rounded-sm glow-orange flex items-center gap-3"
                    >
                        {loading ? "Sending…" : <>Request Wholesale Quote <Send size={15} /></>}
                    </button>
                </form>
            </div>
        </section>
    );
}

function TrustSection() {
    return (
        <section id="trust" className="py-20 border-b border-zinc-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                <Trust icon={Award} title="Certified Technicians" body="Licensed, experienced techs on every install, brake job and repair." />
                <Trust icon={Truck} title="Fast Turnaround" body="Most installs and swaps done same-day. Serving the GTA." />
                <Trust icon={BadgeCheck} title="Canada Compliant · 3PMSF" body="Every tire meets Canada's regulations. All-weather and winter tires are 3PMSF (3 Peak Mountain Snowflake) certified." />
            </div>
        </section>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display text-3xl text-white">{value}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">{label}</div>
        </div>
    );
}

function Trust({ icon: Icon, title, body }) {
    return (
        <div className="flex items-start gap-5">
            <div className="h-12 w-12 border border-zinc-700 flex items-center justify-center shrink-0">
                <Icon size={20} className="text-orange-500" strokeWidth={1.5} />
            </div>
            <div>
                <div className="font-display uppercase tracking-[0.18em] text-white text-base">{title}</div>
                <div className="text-zinc-400 text-sm mt-2 leading-relaxed">{body}</div>
            </div>
        </div>
    );
}
